import type { OnInit } from '@angular/core';
import {
  Inject,
  ChangeDetectionStrategy,
  Component,
  Input,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Feature } from '@freelancer/feature-flags';
import { Location } from '@freelancer/location';
import { ButtonColor } from '@freelancer/ui/button';
import { ContainerSize } from '@freelancer/ui/container';
import { LinkColor, LinkUnderline } from '@freelancer/ui/link';
import { BackgroundColor, LogoSize } from '@freelancer/ui/logo';
import { ModalService, ModalSize } from '@freelancer/ui/modal';
import { StickyBehaviour } from '@freelancer/ui/sticky';
import { TextSize } from '@freelancer/ui/text';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import type { LoggedOutShellConfig } from 'app/app-shell';
import { ShellConfig } from 'app/app-shell';
import type { Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { LoginOrSignup } from '../../login-signup/login-signup-modal';
import {
  CtaButtonType,
  DefaultNavigationLink,
  FreightlancerNavigationLink,
  NavigationAlternate,
} from '../navigation.model';

@Component({
  standalone: false,
  selector: 'app-navigation-logged-out',
  template: `
    <div
      class="NavigationRoot"
      flTrackingSection="NavigationLoggedOut"
      [attr.data-transparent]="transparent"
      [flSticky]="!(config$ | flAsync)?.disableStickyNavBar"
      [flStickyBehaviour]="StickyBehaviour.ALWAYS"
    >
      <fl-container
        class="NavigationContainer"
        [mobilePadding]="true"
        [sizeDesktopXLarge]="containerSizeDesktopXLarge"
        [size]="size"
      >
        <fl-button
          class="LogoButton"
          link="/"
          display="block"
          flTrackingLabel="Logo"
          [attr.data-long]="
            !!alternateNavigation &&
            !!alternateNavigation.logo.mobileDefault &&
            !alternateNavigation.logo.tabletDefault
          "
          [attr.data-long-desktop]="
            !!alternateNavigation &&
            !!alternateNavigation.logo.mobileDefault &&
            !!alternateNavigation.logo.tabletDefault
          "
        >
          @if (!alternateNavigation) {
            <fl-logo
              [backgroundColor]="BackgroundColor.LIGHT"
              [size]="LogoSize.SMALL"
              class="{{ isLoadshift ? 'mr-xsmall' : 'mr-none' }}"
            ></fl-logo>
          }
          @if (alternateNavigation) {
            @if (alternateNavigation && transparent) {
              <fl-picture
                [src]="alternateNavigation.logo.transparent"
                [class.mobile:hide]="!!alternateNavigation.logo.mobileDefault"
                [class.tablet:hide]="!!alternateNavigation.logo.tabletDefault"
              ></fl-picture>
            }
            @if (alternateNavigation && !transparent) {
              <fl-picture
                [src]="alternateNavigation.logo.default"
                [width]="200"
                [class.mobile:hide]="!!alternateNavigation.logo.mobileDefault"
                [class.tablet:hide]="!!alternateNavigation.logo.tabletDefault"
              ></fl-picture>
            }
            @if (alternateNavigation.logo.mobileDefault) {
              @if (transparent) {
                <fl-picture
                  class="desktop:hide tablet:hide"
                  [src]="alternateNavigation.logo.mobileTransparent"
                ></fl-picture>
              }
              @if (!transparent) {
                <fl-picture
                  class="desktop:hide tablet:hide"
                  [src]="alternateNavigation.logo.mobileDefault"
                ></fl-picture>
              }
            }
            @if (alternateNavigation.logo.tabletDefault) {
              @if (transparent) {
                <fl-picture
                  class="desktop:hide mobile:hide"
                  [src]="alternateNavigation.logo.tabletTransparent"
                ></fl-picture>
              }
              @if (!transparent) {
                <fl-picture
                  class="desktop:hide mobile:hide"
                  [src]="alternateNavigation.logo.tabletDefault"
                ></fl-picture>
              }
            }
          }
        </fl-button>
        <ng-container>
          <ng-container *flFeature="Feature.HOW_IT_WORKS_PAGE">
            @if (!alternateNavigation || alternateNavigation.enableLinks) {
              <fl-link
                class="NavigationItem mr-small mobile:hide"
                flTrackingLabel="HowItWorks"
                i18n="Navigation how it works label"
                [link]="'/info/how-it-works/'"
                [size]="TextSize.SMALL"
                [color]="LinkColor.INHERIT"
                [underline]="LinkUnderline.NEVER"
              >
                How It Works
              </fl-link>
            }
          </ng-container>
          <ng-container *flFeature="Feature.SEARCH">
            @if (!alternateNavigation || alternateNavigation.enableLinks) {
              <fl-link
                class="NavigationItem mr-small mobile:hide"
                flTrackingLabel="BrowseJobs"
                i18n="Navigation browse jobs label"
                [link]="
                  isLoadshift
                    ? FreightlancerNavigationLink.BROWSE_LOADS
                    : DefaultNavigationLink.BROWSE
                "
                [size]="TextSize.SMALL"
                [color]="LinkColor.INHERIT"
                [underline]="LinkUnderline.NEVER"
              >
                Browse Jobs
              </fl-link>
            }
            @if (isLoadshift) {
              <fl-link
                class="NavigationItem mr-small mobile:hide"
                flTrackingLabel="LoadshiftEnterprise"
                i18n="Navigation Loadshift enterprise label"
                [link]="'/enterprise'"
                [size]="TextSize.SMALL"
                [color]="LinkColor.INHERIT"
                [underline]="LinkUnderline.NEVER"
              >
                Enterprise
              </fl-link>
            }
          </ng-container>
        </ng-container>
        <div class="NavigationSections">
          @if (!loginSignupModal) {
            <fl-link
              class="NavigationItem mr-small"
              flTrackingLabel="LoginPage"
              i18n="Navigation log in label"
              [link]="'/login'"
              [queryParams]="{ next: redirectUrl$ | flAsync }"
              [size]="TextSize.SMALL"
              [color]="LinkColor.INHERIT"
              [underline]="LinkUnderline.NEVER"
            >
              Log In
            </fl-link>
          }
          @if (loginSignupModal) {
            <fl-link
              class="NavigationItem mr-small"
              flTrackingLabel="LoginModal"
              i18n="Navigation log in label"
              [size]="TextSize.LARGE"
              [color]="LinkColor.INHERIT"
              [underline]="LinkUnderline.NEVER"
              (click)="handleLoginSignupModal(LoginOrSignup.LOGIN)"
            >
              Log In
            </fl-link>
          }
          @if (!loginSignupModal) {
            <fl-link
              class="NavigationItem tablet:mr-small"
              flTrackingLabel="SignUp"
              i18n="Navigation sign up label"
              [size]="TextSize.SMALL"
              [link]="'/signup'"
              [queryParams]="{ next: redirectUrl$ | flAsync }"
              [color]="LinkColor.INHERIT"
              [underline]="LinkUnderline.NEVER"
            >
              Sign Up
            </fl-link>
          }
          @if (loginSignupModal) {
            <fl-link
              class="NavigationItem {{
                disablePostProjectButton ? 'tablet:mr-none' : 'tablet:mr-small'
              }}"
              flTrackingLabel="SignUp"
              i18n="Navigation sign up label"
              [size]="TextSize.LARGE"
              [color]="LinkColor.INHERIT"
              [underline]="LinkUnderline.NEVER"
              (click)="handleLoginSignupModal(LoginOrSignup.SIGNUP)"
            >
              Sign Up
            </fl-link>
          }
          @if (!disablePostProjectButton) {
            @if (!alternateNavigation) {
              <div class="NavigationItem mobile:hide">
                <fl-button
                  i18n="Navigation post project button"
                  flTrackingLabel="PostProjectButton"
                  [color]="ButtonColor.PRIMARY"
                  [link]="
                    isLoadshift
                      ? FreightlancerNavigationLink.POST_LOAD
                      : DefaultNavigationLink.POST_PROJECT
                  "
                >
                  Post a Project
                </fl-button>
              </div>
            }
            @if (alternateNavigation && alternateNavigation.ctaLink) {
              <div class="NavigationItem mobile:hide">
                <ng-container [ngSwitch]="alternateNavigation.ctaButtonType">
                  <fl-button
                    *ngSwitchCase="CtaButtonType.DEMO"
                    i18n="Navigation request enterprise demo button"
                    flTrackingLabel="RequestDemoButton"
                    [color]="ButtonColor.PRIMARY"
                    [link]="alternateNavigation.ctaLink"
                  >
                    Request a Demo
                  </fl-button>
                  <fl-button
                    *ngSwitchCase="CtaButtonType.POST_PROJECT"
                    i18n="Navigation enterprise post a project button"
                    flTrackingLabel="EnterprisePostProjectButton"
                    [color]="ButtonColor.PRIMARY"
                    [link]="alternateNavigation.ctaLink"
                  >
                    Post a Project
                  </fl-button>
                </ng-container>
              </div>
            }
          }
        </div>
      </fl-container>
    </div>
  `,
  styleUrls: ['./navigation-logged-out.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationLoggedOutComponent implements OnInit {
  BackgroundColor = BackgroundColor;
  ButtonColor = ButtonColor;
  Feature = Feature;
  CtaButtonType = CtaButtonType;
  FreightlancerNavigationLink = FreightlancerNavigationLink;
  DefaultNavigationLink = DefaultNavigationLink;
  TextSize = TextSize;
  LinkColor = LinkColor;
  LinkUnderline = LinkUnderline;
  LoginOrSignup = LoginOrSignup;
  LogoSize = LogoSize;
  StickyBehaviour = StickyBehaviour;

  loginSignupModal: boolean;
  size: ContainerSize;
  sizeDesktopXLarge: ContainerSize;
  alternateNavigation: NavigationAlternate;

  @Input() set containerSize(value: ContainerSize) {
    this.size = value || ContainerSize.DESKTOP_LARGE;
  }

  @Input() containerSizeDesktopXLarge?: ContainerSize;

  @Input() set useLoginSignupModal(value: boolean) {
    this.loginSignupModal = value || false;
  }

  @Input() set navigationAlternate(value: NavigationAlternate) {
    this.alternateNavigation = value || false;
  }

  @Input() disablePostProjectButton = false;

  /** T296583: Transparent option is deprecated. It is currently only used by legacy pages for backwards compatibility. */
  @Input() transparent = false;

  redirectUrl$: Observable<string>;
  config$: Observable<LoggedOutShellConfig>;

  isLoadshift: boolean;

  constructor(
    @Inject(UI_CONFIG) private uiConfig: UiConfig,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private modalService: ModalService,
    private router: Router,
    private shellConfig: ShellConfig,
  ) {}

  ngOnInit(): void {
    this.config$ = this.shellConfig.getConfig(this.activatedRoute);

    this.isLoadshift = this.uiConfig.theme === 'freightlancer';

    this.redirectUrl$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.location.url),
      startWith(this.location.url),
      filter(url => new URL(`${this.location.origin}${url}`).pathname !== '/'),
    );
  }

  handleLoginSignupModal(loginOrSignup: LoginOrSignup): void {
    if (this.loginSignupModal) {
      this.modalService.open('LoginSignupModal', {
        inputs: {
          form: loginOrSignup,
        },
        size: ModalSize.XSMALL,
        edgeToEdge: true,
        closeable: true,
        mobileFullscreen: true,
      });
    }
  }
}
